import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { AcquisitionRange } from './analytics-api';

export interface HostCoverage {
  hostname: string;
  aliases: string[];
  status: 'collecting' | 'no_events_in_period' | 'not_collected';
  visitors: number | null;
  pageviews: number | null;
  landing_visitors: number | null;
  cta_visitors: number | null;
  first_collected_at: string | null;
  last_collected_at: string | null;
}
export interface HostSource {
  hostname: string; source: string; medium: string | null; campaign: string | null;
  visitors: number; pageviews: number; landing_visitors: number; cta_visitors: number;
}
export interface HostPage {
  hostname: string; page_path: string | null; visitors: number; pageviews: number;
}
export interface HostReport {
  hosts: HostCoverage[];
  sources: HostSource[];
  // Optional while older backends do not yet provide a page breakdown.
  pages?: HostPage[];
  page_summary?: {
    total_pageviews: number; shown_pageviews: number; omitted_pageviews: number;
    unknown_pageviews: number; limit: number;
  };
  diagnostics: { missing_host_events: number };
  milestones: { leads: number; accounts: number };
  definitions?: Record<string, string>;
}
export const APP_LINK_DEFINITION = 'Distinct tracked browser IDs that clicked a website-to-app link. Not app arrivals or signups.';

export function useHostAcquisition(range: AcquisitionRange, enabled: boolean) {
  const params = new URLSearchParams();
  Object.entries(range).forEach(([key, value]) => { if (value) params.set(key, value); });
  return useQuery({
    queryKey: ['admin', 'host-acquisition', range],
    queryFn: () => api.get<HostReport>(`/v1/admin/organic-acquisition?${params}`),
    enabled, staleTime: 60_000, refetchOnWindowFocus: false,
  });
}
function number(value: number | null | undefined) {
  return typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : 'Unknown';
}
function property(hostname: string) {
  return hostname === 'fantopy.ai' || hostname === 'www.fantopy.ai' ? 'Website' : hostname === 'app.fantopy.ai' ? 'App' : hostname;
}
function sourceLabel(value: string) {
  if (['x', 'twitter', 'x.com', 'twitter.com', 't.co'].includes(value.toLowerCase())) return 'X / Twitter';
  if (/^(facebook|fb|(?:www\.|m\.|l\.|lm\.)?facebook\.com)$/i.test(value)) return 'Facebook';
  return value === 'direct' ? 'Direct / unknown' : value;
}
const properties = [
  { hostname: 'fantopy.ai', label: 'Website' },
  { hostname: 'app.fantopy.ai', label: 'App' },
];

function PageViews({ report, loading, error }: { report: HostReport | undefined; loading: boolean; error: unknown }) {
  const pages = report?.pages;
  const summary = report?.page_summary;
  return <section aria-labelledby="page-views-title" className="min-w-0 space-y-2">
    <h3 id="page-views-title" className="font-medium">Page views by page</h3>
    {loading ? <p role="status" className="text-sm">Loading page views…</p>
      : error || !pages || !summary ? <p className="text-sm">Page breakdown unavailable. Page counts are unknown, not zero.</p>
      : <>
        <div role="region" aria-label="Page views by page table" tabIndex={0} className="max-w-full overflow-x-auto rounded-md border">
          <table className="w-full min-w-[360px] text-left text-sm">
            <thead><tr>{['Page', 'Website / App', 'Page views'].map(label => <th scope="col" key={label} className="border-b p-3">{label}</th>)}</tr></thead>
            <tbody>{pages.map((page, index) => <tr key={`${page.hostname}:${page.page_path}:${index}`}>
              <td className="max-w-[28rem] break-all border-b p-3">{page.page_path || 'Unknown page'}</td>
              <td className="border-b p-3">{property(page.hostname)}</td>
              <td className="border-b p-3 tabular-nums">{number(page.pageviews)}</td>
            </tr>)}</tbody>
            <tfoot>
              <tr><th scope="row" colSpan={2} className="p-3 font-normal">Shown page views</th><td className="p-3 tabular-nums">{number(summary.shown_pageviews)}</td></tr>
              <tr><th scope="row" colSpan={2} className="p-3 font-normal">Omitted page views (beyond {number(summary.limit)} page groups)</th><td className="p-3 tabular-nums">{number(summary.omitted_pageviews)}</td></tr>
              <tr className="border-t font-semibold"><th scope="row" colSpan={2} className="p-3">Total page views</th><td className="p-3 tabular-nums">{number(summary.total_pageviews)}</td></tr>
              <tr><th scope="row" colSpan={2} className="p-3 font-normal">Of total: missing page path</th><td className="p-3 tabular-nums">{number(summary.unknown_pageviews)}</td></tr>
            </tfoot>
          </table>
        </div>
        {summary.total_pageviews === 0 && <p className="text-sm">No host-labelled page views recorded in this period.</p>}
        <p className="text-xs text-muted-foreground">Dynamic identifiers are anonymized as :id. Shown + omitted = total for Website and App in the selected scope. Missing paths are included in the total, not extra views; they appear as Unknown page when shown. {summary.omitted_pageviews > 0 ? 'The breakdown is truncated; omitted views may include missing paths.' : 'All page groups are shown.'}</p>
      </>}
  </section>;
}

export function HostAcquisition({ report, loading, error }: { report: HostReport | undefined; loading: boolean; error: unknown }) {
  // Do not display cached counts as current after an error or while changing scope.
  const data = loading || error ? undefined : report;
  // A legacy split-host response cannot safely be merged into unique browsers.
  const splitWebsite = data?.hosts?.some(host => host.hostname === 'www.fantopy.ai');
  return <div className="min-w-0 space-y-4">
    {loading ? <p role="status">Loading collection diagnostics…</p> : (!data?.hosts || error) ? <p>Collection status unknown — backend host diagnostics could not be read. This is not zero traffic.</p> : null}
    <div className="grid min-w-0 gap-3 sm:grid-cols-2">{properties.map(({ hostname, label }) => {
      const host = hostname === 'fantopy.ai' && splitWebsite ? undefined : data?.hosts?.find(item => item.hostname === hostname);
      return <section aria-label={`${label} coverage`} key={hostname} className="min-w-0 rounded-md border p-3">
        <h3 className="font-medium">{label}</h3>
        <p className="break-words text-xs text-muted-foreground">{hostname}</p>
        {host?.aliases?.length ? <p className="mt-1 break-words text-xs text-muted-foreground">Collected hostnames: {host.aliases.join(', ')}</p> : null}
        <p className="mt-2 text-sm text-muted-foreground">{loading ? 'Loading…' : host?.status === 'not_collected' ? 'Not collected' : host?.status === 'collecting' ? 'Events recorded in period' : host?.status === 'no_events_in_period' ? 'No events in selected period' : 'Unknown'}</p>
        <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <dt>Recorded browsers</dt><dd className="text-right tabular-nums">{number(host?.visitors)}</dd>
          <dt>Page views</dt><dd className="text-right tabular-nums">{number(host?.pageviews)}</dd>
        </dl>
        {host?.last_collected_at && <p className="mt-2 text-xs text-muted-foreground">Last accepted event: {new Date(host.last_collected_at).toLocaleString()}</p>}
      </section>;
    })}</div>
    {splitWebsite && <p className="text-sm">Combined Website counts unavailable from this backend. Split-host browser counts are not added together.</p>}
    <p className="text-xs text-muted-foreground">Recorded browsers are consented browser IDs, not people or sessions. Do not sum host visitors as unique people: Website and App IDs are independent. Not collected means no accepted host-labelled events; no events in a known collection period is a measured zero.</p>
    <PageViews report={data} loading={loading} error={error} />
    {data?.diagnostics && <p className="text-xs text-muted-foreground">Missing destination host: {number(data.diagnostics.missing_host_events)} historical landing/page-view events in this period, excluded from the property and page breakdowns. Their host and page are not inferred.</p>}
    {data?.sources && <section className="min-w-0 space-y-2" aria-labelledby="property-origins-title">
      <h3 id="property-origins-title" className="font-medium">Traffic origins by property</h3>
      <p className="text-xs text-muted-foreground">Clicked app link: {APP_LINK_DEFINITION}</p>
      <div role="region" aria-label="Traffic origins by property table" tabIndex={0} className="max-w-full overflow-x-auto">
        <table className="w-full text-left text-sm"><thead><tr>{['Destination host', 'Origin', 'Medium', 'Campaign', 'Recorded browsers', 'Page views', 'Landing visitors', 'Clicked app link'].map(label => <th scope="col" className="whitespace-nowrap border-b p-2" key={label}>{label}</th>)}</tr></thead>
          <tbody>{data.sources.map((row, index) => <tr key={index}>
            <td className="border-b p-2">{property(row.hostname)}<div className="text-xs text-muted-foreground">{row.hostname}</div></td>
            <td className="border-b p-2">{sourceLabel(row.source)}</td><td className="border-b p-2">{row.medium || 'Unknown'}</td><td className="border-b p-2">{row.campaign || 'Not tagged'}</td>
            {[row.visitors, row.pageviews, row.landing_visitors, row.cta_visitors].map((value, column) => <td key={column} className="border-b p-2 tabular-nums">{number(value)}</td>)}
          </tr>)}</tbody></table>
      </div>
      {data.sources.length === 0 && <p className="py-3 text-sm">No host-labelled traffic origins recorded in this period.</p>}
      <p className="text-xs text-muted-foreground">Up to 100 groups. Earliest period landing per collected hostname/browser ID, otherwise earliest page view; UTM source → referrer host → direct / unknown. Source labels are not proof of paid traffic.</p>
    </section>}
    {data?.milestones && <p className="text-xs text-muted-foreground">Server-confirmed leads: {number(data.milestones.leads)} · account links: {number(data.milestones.accounts)} across this game and period. Leads count distinct user IDs with server email_submitted; accounts count server account_linked. App-link clicks are neither leads nor accounts. Milestones are not assigned to properties without identity linkage.</p>}
  </div>;
}
